//@ts-nocheck
import { GlobalVariables } from './constants';
import { getRuntimeConfig } from './runtime-config';
import Cookies from 'js-cookie';
import { showToast } from '@banno/jha-wc/src/js/utils/toast-util.js';
import { StatusConstants } from './apiStatusConstants';
import { removeAllCookies } from './removeAllCookies';
import { openForcedLogoutNotice } from '../Components/JhftJhaDialog';
import { decryptValue, isSessionExpired } from './app-utils';

// ---------------------------------------------------------------------------
// LOCAL DEVELOPMENT ONLY — no effect on prod, test, staging, or any deployed UI
//
// This block (together with matching variables in your machine's .env.local) exists
// solely to make local work easier: debug the UI against localhost while mixing in
// remote dev APIs for services you are not running, with minimal setup.
//
// • .env.local is gitignored; it is not in the repo and is not shipped with builds.
// • CI and deployment pipelines use their own env — not your .env.local.
// • Even if VITE_USE_DEV_REFERENCE_DATA were true in a build, URL rewriting below
//   only runs when the browser hostname is localhost or 127.0.0.1 (see _isLocalhostDev).
//   Deployed apps never hit that branch, so there is no production security surface
//   from this feature — it is a developer ergonomics aid only.
//
// Behavior when enabled locally: redirect API calls that would go to the dev
// environment unless the path matches VITE_LOCAL_API_PATHS (then route to a local port).
// Variables: VITE_USE_DEV_REFERENCE_DATA, VITE_DEV_REFERENCE_DATA_URL, VITE_LOCAL_API_PATHS.
//
// Every VITE_LOCAL_API_PATHS entry is "segment:port" (port is required):
//   "template:7025,workflow:7025"                → Workflow API on 7025
//   "template:7025,workflow:7025,user:7052"      → + Users API on 7052
//   "template:7025,workflow:7025,businessunit:7201" → + BU API on 7201
// ---------------------------------------------------------------------------
const _isLocalhostDev: boolean =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
  (import.meta as any).env?.VITE_USE_DEV_REFERENCE_DATA === 'true';

const _devApiBase: string | undefined = _isLocalhostDev
  ? (import.meta as any).env?.VITE_DEV_REFERENCE_DATA_URL
  : undefined;

const _localApiBase: string | undefined = _isLocalhostDev
  ? (import.meta as any).env?.VITE_API_BASE_URL
  : undefined;

interface LocalPathEntry {
  segment: string;
  port: string;
}

/**
 * Parsed list of API URL “segments” that should stay on your machine during local dev.
 *
 * When `_isLocalhostDev` is false (any deployed build or missing env flags), this is
 * always `[]` — the parser never runs.
 *
 * When true, reads `VITE_LOCAL_API_PATHS` from `.env.local`, e.g.
 *   `workflow:7025,template:7025,businessunit:7201`
 * Each comma-separated token must look like `segment:port` where:
 *   • `segment` is the path piece after `/{institutionId}/` (see `resolveApiUrl` — it matches
 *     `/${instId}/${entry.segment}/` or `/${instId}/reference/${entry.segment}/`, so `user:7052`
 *     also covers User Service lookups like `/reference/user/userrole`).
 *   • `port` is numeric only (digits after the last `:`).
 *
 * `lastIndexOf(':')` picks the **final** colon so a segment name itself must not contain `:`.
 * Bad tokens are dropped after `console.warn`; they are never sent to the server.
 */
const _localApiPaths: LocalPathEntry[] = _isLocalhostDev
  ? (((import.meta as any).env?.VITE_LOCAL_API_PATHS || '')
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean)
    .map((entry: string): LocalPathEntry | null => {
      const colonIdx = entry.lastIndexOf(':');
      const afterColon = entry.slice(colonIdx + 1);
      // Require "something:12345" — segment non-empty, port purely digits
      if (colonIdx > 0 && /^\d+$/.test(afterColon)) {
        return { segment: entry.slice(0, colonIdx), port: afterColon };
      }
      console.warn(
        `[jha-fetch] Invalid VITE_LOCAL_API_PATHS entry "${entry}" — expected "segment:port" (e.g. "workflow:7025")`
      );
      return null;
    })
    .filter(Boolean) as LocalPathEntry[])
  : [];

function resolveApiUrl(url: string): string {
  const instId = getRuntimeConfig().institutionUniversalId;
  if (!_isLocalhostDev || !_devApiBase || !_localApiBase) return url;
  if (!url.startsWith(_localApiBase)) return url;

  const match = _localApiPaths.find(
    (entry) =>
      url.includes(`/${instId}/${entry.segment}/`) ||
      url.includes(`/${instId}/reference/${entry.segment}/`)
  );

  if (!match) {
    return url.replace(_localApiBase, _devApiBase);
  }

  try {
    const parsed = new URL(_localApiBase);
    parsed.port = match.port;
    return url.replace(_localApiBase, parsed.origin);
  } catch {
    return url;
  }
}

// Per-request auth: on localhost only, prefer VITE_JHA_SSO_TOKEN from .env.local over
// cookie (same local-dev-only story as above — not used for deployed sites).
function getAuthToken(): string | undefined {
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const fromCookie = Cookies.get('token');
  const envToken = isLocalhost ? (import.meta as any).env?.VITE_JHA_SSO_TOKEN : undefined;

  // On localhost, prefer VITE_JHA_SSO_TOKEN over cookie (cookie may be stale)
  if (isLocalhost && envToken) {
    return envToken;
  }

  // Fall back to cookie
  if (fromCookie) {
    return fromCookie;
  }

  return undefined;
}
function getUserId(): string | undefined {
  const fromCookie = Cookies.get('userId');
  if (fromCookie) return decryptValue(fromCookie ?? '') || undefined;
  // VITE_DEV_USER_ID from .env.local: localhost-only fallback (see header comment above).
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    const envUserId = (import.meta as any).env?.VITE_DEV_USER_ID;
    if (envUserId) return envUserId;
  }
  return undefined;
}

export async function postRequest<T = {}>(
  url: string,
  request: T,
  headers?: any,
  suppressFailureToast?: boolean
) {
  if (isSessionExpired()) {
    onSessionTimedOut();
  } else {
    try {
      const token = getAuthToken();
      const requestHeaders = headers || {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
        'X-Frame-Options': 'sameorigin',
        'Content-Security-Policy': 'self',
        userId: getUserId(),
      };

      const response = await fetch(resolveApiUrl(url), {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: requestHeaders,
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
        body: JSON.stringify(request),
      });

      if (
        response?.status === StatusConstants.NOT_FOUND_USER_CODE ||
        response?.status === StatusConstants.UNAUTHORIZED_USER_CODE
      ) {
        return response;
      } else {
        return response.json();
      }
    } catch (e) {
      if (!suppressFailureToast) {
        showToast({
          message: 'Something went wrong, please try again.',
          role: 'danger',
          duration: GlobalVariables.TOAST_TIMEOUT,
        });
      }
    }
  }
}
export async function postFileRequest<T = {}>(url: string, request: T, headers?: any) {
  if (isSessionExpired()) {
    onSessionTimedOut();
  } else {
    try {
      const response = await fetch(resolveApiUrl(url), {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'same-origin',
        headers: headers || {
          Authorization: 'Bearer ' + getAuthToken(),
          'Access-Control-Allow-Origin': '*',
          'Accept-Post': '*/*',
          'Content-Type': 'application/json',
          'X-Frame-Options': 'sameorigin',
          'Content-Security-Policy': 'self',
          userId: getUserId(),
        },
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
        body: JSON.stringify(request),
      });
      if (response.ok) {
        return response.blob();
      } else if (response?.status === StatusConstants.NOT_FOUND_USER_CODE) {
        return response;
      } else {
        return response.json();
      }
    } catch (e) {
      showToast({
        message: 'Something went wrong, please try again.',
        role: 'danger',
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    }
  }
}
export function fixedEncodeURIComponent(str: string) {
  return encodeURIComponent(str).replace(/[!'()*]/g, function (c) {
    return '%' + c.charCodeAt(0).toString(16);
  });
}
export async function getRequest<T = unknown>(url: string, request?: T, headers?: any) {
  if (isSessionExpired()) {
    onSessionTimedOut();
  } else {
    try {
      const response = await fetch(
        resolveApiUrl(url) + encodeURIComponent((request as any) || ''),
        {
          method: 'GET',
          headers: headers || {
            Authorization: 'Bearer ' + getAuthToken(),
            'X-Frame-Options': 'sameorigin',
            'Content-Security-Policy': 'self',
            userId: getUserId(),
          },
        }
      );
      if (
        response?.status === StatusConstants.NOT_FOUND_BUSINESSUNITID_CODE ||
        response?.status === StatusConstants.UNAUTHORIZED_USER_CODE
      ) {
        return response;
      } else {
        return response.json();
      }
    } catch (e) {
      showToast({
        message: 'Something went wrong, please try again.',
        role: 'danger',
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    }
  }
}
export async function putRequest<T = unknown>(url: string, eTag?: string, request?: T, anonymous = false) {
  // Invite / reset links are public. The admin session must not ride along:
  // an expired cookie blocks the call, and the bearer can stamp the caller's type.
  if (!anonymous && isSessionExpired()) {
    onSessionTimedOut();
  } else {
    try {
      const authHeaders = anonymous
        ? {}
        : {
          Authorization: 'Bearer ' + getAuthToken(),
          userId: getUserId(),
        };
      const response = await fetch(resolveApiUrl(url), {
        method: 'PUT',
        headers: eTag
          ? {
            ...authHeaders,
            'If-Match': eTag,
            'Content-Type': 'application/json',
            'X-Frame-Options': 'sameorigin',
            'Content-Security-Policy': 'self',
          }
          : {
            ...authHeaders,
            'Content-Type': 'application/json',
            'X-Frame-Options': 'sameorigin',
            'Content-Security-Policy': 'self',
          },
        body: JSON.stringify(request),
      });
      // Handle empty response (204 No Content or empty body)
      const text = await response.text();
      if (response.ok && (response.status === 204 || !text?.trim())) {
        return { messageStatus: null };
      }
      if (!text?.trim()) {
        return { messageStatus: 'error', status: response.status };
      }
      const parsed = JSON.parse(text);
      if (!response.ok && parsed && typeof parsed === 'object') {
        if (parsed.messageStatus == null) {
          parsed.messageStatus = 'error';
        }
        if (parsed.status == null) {
          parsed.status = response.status;
        }
      }
      return parsed;
    } catch (e) {
      console.error('[jha-fetch] PUT error:', e);
      showToast({
        message: 'Something went wrong, please try again.',
        role: 'danger',
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    }
  }
}
export async function deleteRequest(url: string, eTag: string, headers?: any) {
  if (isSessionExpired()) {
    onSessionTimedOut();
  } else {
    try {
      const response = await fetch(resolveApiUrl(url), {
        method: 'DELETE',
        headers: headers || {
          Authorization: 'Bearer ' + getAuthToken(),
          'If-Match': eTag,
          'X-Frame-Options': 'sameorigin',
          'Content-Security-Policy': 'self',
          userId: getUserId(),
        },
      });
      return response;
    } catch (e) {
      showToast({
        message: 'Something went wrong, please try again.',
        role: 'danger',
        duration: GlobalVariables.TOAST_TIMEOUT,
      });
    }
  }
}
/**
 * Every request made after the token expires lands here; they share one notice.
 * Cookies stay until the notice is dismissed: clearing them first would let the shell's
 * shared-session check redirect before the user sees why.
 */
export async function onSessionTimedOut() {
  try {
    await openForcedLogoutNotice('sessionExpired');
  } catch {
    /* callers do not await; log out regardless */
  }
  removeAllCookies();
  window.location.href = '/login';
}
